import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import Modal from "react-modal";
import ZaloPaySDK from "utils/zalopay";
import Utils from "utils/utils";
import "./CarInsuranceScreen.scss";
import { useDispatch, useSelector } from "react-redux";
import { getInsuranceCarProduct } from "stores/insurance/insurance.action";
import { clearErrMess, clearPaymentInfo, createOrderCallback, payOrder } from "stores/payment/payment.action";
import { getCities } from "stores/address/address.action";
import { LIST_CAR_TYPE, PRODUCT_TYPE, TYPE_INSURANCE } from "utils/const";
import { updateCustomerInfo } from "stores/customer/customer.action";
import { clearCarBrand, getListBrandModelCarByCompany } from "stores/carInfomation/carInfomation.action";
import {
  ACCIDENT_INSURANCE_OPTIONS,
  BENEFIT_SHEET_TABS,
  MIN_DAYS_FROM_NOW,
  PROVINCE_FALLBACK_LIST,
  SCREEN_TITLE,
} from "./constants";
import CarTierBar from "./components/CarTierBar";
import CarInsurancePanel from "./components/CarInsurancePanel";
import CarOwnerInfoSection from "./components/CarOwnerInfoSection";
import CarBenefitSheetModal from "./components/CarBenefitSheetModal";

const CarInsuranceScreen = () => {
  const dispatch = useDispatch();

  // ─── Redux Selectors ─────────────────────────────────────────────
  const {
    gettingInsuranceCarProduct,
    insuranceCarProduct,
    getInsuranceCarProductErrMsg,
  } = useSelector((state) => state.insurance);

  const {
    createPayOrder,
    isPayment,
    paymentInfo,
    createPayOrderErrMsg,
    creatingOrder,
    createOrderErrMsg,
  } = useSelector((state) => state.payment);

  const { customerInfo } = useSelector((state) => state.customer);
  const { userInfo } = useSelector((state) => state.appParams);
  const { brands } = useSelector((state) => state.carInfomation);
  const { cities } = useSelector((state) => state.address);

  // ─── Component State ──────────────────────────────────────────────
  // 1. Xe ô tô Category Pill / TierBar (default LIST_CAR_TYPE[0])
  const [selectedCarType, setSelectedCarType] = useState(LIST_CAR_TYPE[0]);

  // 2. Gói & Công ty & Bảo hiểm tai nạn người ngồi trên xe
  const [seats, setSeats] = useState("5"); // Giá trị UI (khi đang nhập)
  const [seatsCommitted, setSeatsCommitted] = useState("5"); // Giá trị dùng để call API (khi blur)
  const [selectedCompanyCode, setSelectedCompanyCode] = useState("MIC"); // Mặc định công ty MIC
  const [accidentInsurance, setAccidentInsurance] = useState(ACCIDENT_INSURANCE_OPTIONS[0]); // Mặc định: "Không"

  // 3. Ngày hiệu lực
  const minDateEffect = useMemo(
    () => moment().add(MIN_DAYS_FROM_NOW, "days").startOf("date").toDate(),
    []
  );
  const maxDateEffect = useMemo(
    () => moment().startOf("date").add(365 + MIN_DAYS_FROM_NOW, "days").toDate(),
    []
  );
  const [dateEffect, setDateEffect] = useState(minDateEffect);

  // 4. Biểu mẫu Thông tin xe & Chủ xe & Khách hàng & Hóa đơn VAT
  const initialBuyerName = userInfo?.user_name || userInfo?.displayName || customerInfo?.fullName || "";
  const initialBuyerPhone = userInfo?.user_phone || customerInfo?.phone || "";
  const initialEmail = userInfo?.user_email || customerInfo?.email || "";
  const initialAddress = userInfo?.permanent_address || customerInfo?.address || "";
  const initialCarOwner = customerInfo?.carOwner ? customerInfo.carOwner.toUpperCase() : initialBuyerName.toUpperCase();

  const [form, setForm] = useState({
    isOwner: true,
    carOwner: initialCarOwner,
    licensePlates: customerInfo?.licensePlates ? customerInfo.licensePlates.toUpperCase() : "",
    carBrand: {},
    carModel: {},
    chassisNo: customerInfo?.chassisNo ? customerInfo.chassisNo : "",
    engineNo: customerInfo?.engineNo ? customerInfo.engineNo : "",
    buyerName: initialBuyerName,
    buyerPhone: initialBuyerPhone,
    email: initialEmail,
    address: initialAddress,
    // Thông tin xuất hóa đơn
    hasInvoice: false,
    invoiceCompanyName: "",
    invoiceTaxCode: "",
    invoiceCompanyAddress: "",
    invoicePhone: "",
    invoiceEmail: "",
    invoiceRecipientName: "",
    invoiceProvince: {},
  });

  const [fieldTouched, setFieldTouched] = useState({
    carOwner: false,
    licensePlates: false,
    carBrand: false,
    carModel: false,
    chassisNo: false,
    engineNo: false,
    buyerName: false,
    buyerPhone: false,
    email: false,
    address: false,
    invoiceCompanyName: false,
    invoiceTaxCode: false,
    invoiceCompanyAddress: false,
    invoicePhone: false,
    invoiceEmail: false,
    invoiceRecipientName: false,
    invoiceProvince: false,
  });

  // 5. Data & Loadings cho Dropdown Select (Brands, Models, Provinces)
  const [carBrandData, setCarBrandData] = useState([]);
  const [carModelData, setCarModelData] = useState([]);

  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);

  // 6. Modal Quyền lợi
  const [benefitSheetOpen, setBenefitSheetOpen] = useState(false);
  const [benefitSheetTab, setBenefitSheetTab] = useState(BENEFIT_SHEET_TABS.detail);

  const closeBenefitSheet = () => setBenefitSheetOpen(false);
  const openBenefitSheet = (tab) => {
    setBenefitSheetTab(tab || BENEFIT_SHEET_TABS.detail);
    setBenefitSheetOpen(true);
  };

  // ─── Set Title V2 via SDK & Fetch Cities & Clear Stale Brands ──────
  useEffect(() => {
    ZaloPaySDK.setTitleV2(SCREEN_TITLE);
    dispatch(getCities());
    dispatch(clearCarBrand());
  }, [dispatch]);

  // Cities / Provinces list mapping
  const provincesData = useMemo(() => {
    if (Array.isArray(cities) && cities.length > 0) {
      return cities.map((c) => ({
        code: c.code || c.id,
        name: c.name,
        nameWithType: c.nameWithType || c.name,
      }));
    }
    return PROVINCE_FALLBACK_LIST;
  }, [cities]);

  // Modal App Element setup
  useEffect(() => {
    const root = document.getElementById("root");
    if (root) Modal.setAppElement("#root");
  }, []);

  // Prevent background scroll when modal open
  useEffect(() => {
    if (benefitSheetOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [benefitSheetOpen]);

  // Ref đánh dấu đã auto-fill từ thông tin người dùng (tránh ghi đè khi user tự xóa nội dung)
  const autoFilledRef = useRef(
    Boolean(initialBuyerName || initialBuyerPhone || initialEmail || initialAddress)
  );

  // Auto-fill từ userInfo 1 lần duy nhất khi có dữ liệu OAuth trả về (nếu form chưa có dữ liệu)
  useEffect(() => {
    if (autoFilledRef.current) return;
    const userName = userInfo?.user_name || userInfo?.displayName || "";
    const userPhone = userInfo?.user_phone || "";
    const userEmail = userInfo?.user_email || "";
    const userAddress = userInfo?.permanent_address || "";

    if (userName || userPhone || userEmail || userAddress) {
      autoFilledRef.current = true;
      setForm((prev) => {
        const nextBuyerName = prev.isOwner ? (prev.buyerName || userName) : prev.buyerName;
        const nextBuyerPhone = prev.isOwner ? (prev.buyerPhone || userPhone) : prev.buyerPhone;
        const nextEmail = prev.isOwner ? (prev.email || userEmail) : prev.email;
        const nextAddress = prev.address || userAddress;
        const nextCarOwner = prev.carOwner || (userName ? userName.toUpperCase() : "");

        return {
          ...prev,
          buyerName: nextBuyerName,
          buyerPhone: nextBuyerPhone,
          email: nextEmail,
          address: nextAddress,
          carOwner: nextCarOwner,
        };
      });
    }
  }, [userInfo]);

  // Sync Form to Redux customerInfo
  useEffect(() => {
    if (!customerInfo) return;
    const updated = {
      ...customerInfo,
      carOwner: form.carOwner,
      licensePlates: form.licensePlates,
      carBrand: form.carBrand,
      carModel: form.carModel,
      chassisNo: form.chassisNo,
      engineNo: form.engineNo,
      fullName: form.buyerName,
      phone: form.buyerPhone,
      email: form.email,
      address: form.address,
    };
    dispatch(updateCustomerInfo(updated));
  }, [form, dispatch]);

  // ─── Fetch Product Fees via Redux ──────────────────────────────────
  // Chỉ call API khi seatsCommitted, loại xe, hoặc bảo hiểm tai nạn thay đổi
  useEffect(() => {
    const stateAmountInsurances = accidentInsurance?.code === "0" ? {} : accidentInsurance;
    const data = {
      usePurpose: selectedCarType?.code?.usePurpose || "non_commercial",
      goodsCap: 0,
      carInfo: (selectedCarType?.code?.carInfo || 1) + "",
      stateAmountInsurances: stateAmountInsurances,
      seat: Number(seatsCommitted) || 5,
    };
    dispatch(getInsuranceCarProduct({ data }));
  }, [selectedCarType, seatsCommitted, accidentInsurance, dispatch]);

  // ─── Reset Hãng xe, Hiệu xe khi change công ty bảo hiểm ───────────
  const prevCompanyCodeRef = useRef(selectedCompanyCode);
  useEffect(() => {
    if (prevCompanyCodeRef.current !== selectedCompanyCode) {
      prevCompanyCodeRef.current = selectedCompanyCode;
      setForm((prev) => ({
        ...prev,
        carBrand: {},
        carModel: {},
      }));
      setFieldTouched((prev) => ({
        ...prev,
        carBrand: false,
        carModel: false,
      }));
      setCarBrandData([]);
      setCarModelData([]);
    }
  }, [selectedCompanyCode]);

  // ─── Fetch Car Brands by Company Code ──────────────────────────────
  useEffect(() => {
    if (!selectedCompanyCode) {
      setCarBrandData([]);
      setLoadingBrands(false);
      return;
    }

    let isMounted = true;
    setLoadingBrands(true);
    setCarBrandData([]); // Reset danh sách trước khi fetch

    dispatch(
      getListBrandModelCarByCompany(
        { companyCode: selectedCompanyCode },
        (resp, isErr) => {
          if (!isMounted) return;
          setLoadingBrands(false);

          // Nếu call lỗi hoặc data rỗng thì không lấy data mặc định, hiển thị data rỗng
          if (isErr || !resp) {
            setCarBrandData([]);
            return;
          }

          const brandList = resp.carBrand || resp.brand;
          if (Array.isArray(brandList) && brandList.length > 0) {
            const mapped = brandList.map((b) => ({
              code: b.key || b.id || b.name,
              name: b.name,
              nameWithType: b.name,
            }));
            setCarBrandData(mapped);
          } else {
            // Data rỗng -> không lấy data mặc định, hiển thị data rỗng
            setCarBrandData([]);
          }
        }
      )
    );

    return () => {
      isMounted = false;
    };
  }, [selectedCompanyCode, dispatch]);

  // Sync brand list from Redux store `brands` if matching company
  useEffect(() => {
    if (brands && brands.companyCode === selectedCompanyCode) {
      if (Array.isArray(brands.carBrand) && brands.carBrand.length > 0) {
        const mapped = brands.carBrand.map((b) => ({
          code: b.key || b.id || b.name,
          name: b.name,
          nameWithType: b.name,
        }));
        setCarBrandData(mapped);
      } else {
        setCarBrandData([]);
      }
    }
  }, [brands, selectedCompanyCode]);

  // Handle Car Brand Selection → Fetch Models
  const handleBrandChange = (brandItem) => {
    setForm((prev) => ({
      ...prev,
      carBrand: brandItem || {},
      carModel: {}, // Reset model on brand change
    }));
    setFieldTouched((prev) => ({
      ...prev,
      carModel: false,
    }));
    setCarModelData([]);

    if (brandItem?.code && selectedCompanyCode) {
      setLoadingModels(true);
      dispatch(
        getListBrandModelCarByCompany(
          { companyCode: selectedCompanyCode, brand: brandItem.code },
          (resp, isErr) => {
            setLoadingModels(false);
            // Nếu call lỗi hoặc data rỗng thì không lấy data mặc định, hiển thị data rỗng
            if (isErr || !resp) {
              setCarModelData([]);
              return;
            }

            const modelList = resp.carModel || resp.model;
            if (Array.isArray(modelList) && modelList.length > 0) {
              const mapped = modelList.map((m) => ({
                code: m.key || m.id || m.name,
                name: m.name,
                nameWithType: m.name,
              }));
              setCarModelData(mapped);
            } else {
              setCarModelData([]);
            }
          }
        )
      );
    }
  };

  const handleModelChange = (modelItem) => {
    setForm((prev) => ({ ...prev, carModel: modelItem || {} }));
  };

  // ─── Extract Companies List from Redux insuranceCarProduct (MIC ở đầu) ───
  const companies = useMemo(() => {
    const rawCarProducts = insuranceCarProduct?.[TYPE_INSURANCE.CAR] || [];
    if (!rawCarProducts.length) {
      // Fallback list when API data is loading or empty (MIC ở trên đầu)
      return [
        { code: "MIC", name: "Bảo hiểm Quân đội (MIC)", premium: 480700 },
        { code: "BSH", name: "Bảo hiểm BSH", premium: 480700 },
        { code: "PVI", name: "Bảo hiểm PVI", premium: 480700 },
        { code: "VNI", name: "Bảo hiểm VNI", premium: 480700 },
      ];
    }

    const map = {};
    rawCarProducts.forEach((p) => {
      const code = (p.company?.code || p.company?.id || p.partnerCode || "MIC").toUpperCase();
      if (!map[code]) {
        map[code] = {
          id: p.company?.id || code,
          code: code,
          name: p.company?.name || `Bảo hiểm ${code}`,
          image: p.company?.image || p.image,
          premium: p.premium || 480700,
          product: p,
        };
      }
    });

    const result = Object.values(map);
    // Sắp xếp công ty MIC ở trên đầu
    result.sort((a, b) => {
      if (a.code === "MIC") return -1;
      if (b.code === "MIC") return 1;
      return 0;
    });

    return result;
  }, [insuranceCarProduct]);

  // Selected company object & price
  const selectedCompanyObj = useMemo(() => {
    const found = companies.find(
      (c) => c.code === selectedCompanyCode || c.id === selectedCompanyCode
    );
    return found || companies[0] || { code: "MIC", name: "Bảo hiểm Quân đội (MIC)", premium: 480700 };
  }, [companies, selectedCompanyCode]);

  const selectedProduct = useMemo(() => {
    return selectedCompanyObj?.product || null;
  }, [selectedCompanyObj]);

  const totalFee = selectedCompanyObj?.premium || 480700;

  // ─── Loading / Error SDK dialogs ──────────────────────────────────
  useEffect(() => {
    if (gettingInsuranceCarProduct || creatingOrder || createPayOrder) {
      ZaloPaySDK.showLoading();
    } else {
      ZaloPaySDK.hideLoading();
    }
  }, [gettingInsuranceCarProduct, creatingOrder, createPayOrder]);

  useEffect(() => {
    if (getInsuranceCarProductErrMsg) {
      ZaloPaySDK.showDialog({
        message: getInsuranceCarProductErrMsg,
        buttons: [{ text: "Đóng" }],
      });
    }
  }, [getInsuranceCarProductErrMsg]);

  useEffect(() => {
    if (createOrderErrMsg) {
      ZaloPaySDK.showDialog({
        message: createOrderErrMsg,
        buttons: [{ text: "Đóng" }],
      });
      dispatch(clearErrMess());
    }
  }, [createOrderErrMsg, dispatch]);

  useEffect(() => {
    if (createPayOrderErrMsg) {
      ZaloPaySDK.showDialog({
        message: createPayOrderErrMsg,
        buttons: [{ text: "Đóng" }],
      });
    }
  }, [createPayOrderErrMsg]);

  // Payment callback
  const onPaymentCallback = (data) => {
    console.log("Payment Callback:", data);
    dispatch(clearPaymentInfo());
  };

  useEffect(() => {
    if (paymentInfo && isPayment) {
      ZaloPaySDK.payOrder(paymentInfo, onPaymentCallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentInfo, isPayment]);

  // ─── Validations ──────────────────────────────────────────────────
  const seatsError = useMemo(() => {
    const num = Number(seats);
    if (!seats || isNaN(num) || num <= 0) return "Vui lòng nhập số chỗ ngồi";
    if (selectedCompanyCode === "MIC" && num < 4) {
      return "Bảo hiểm MIC bắt buộc từ 4 chỗ ngồi trở lên";
    }
    if (selectedCarType?.code?.carInfo === 19 && (num < 2 || num > 5)) {
      return "Xe vừa chở người vừa chở hàng: từ 2 đến 5 chỗ";
    }
    if (num < 2 || num > 50) return "Số chỗ ngồi hợp lệ từ 2 đến 50";
    return "";
  }, [seats, selectedCarType, selectedCompanyCode]);

  // Commit seats khi blur để trigger API call
  const handleSeatsBlur = () => {
    if (!seatsError) {
      setSeatsCommitted(seats);
    }
  };

  const carOwnerError = useMemo(() => {
    if (!fieldTouched.carOwner) return "";
    if (!form.carOwner?.trim()) return "Vui lòng nhập họ tên chủ xe";
    if (!Utils.validateName(form.carOwner.trim())) return "Họ tên chủ xe không hợp lệ";
    return "";
  }, [form.carOwner, fieldTouched.carOwner]);

  const licensePlatesError = useMemo(() => {
    if (!fieldTouched.licensePlates) return "";
    if (!form.licensePlates?.trim()) return "Vui lòng nhập biển số xe";
    if (Utils.validateCharSpecial(form.licensePlates)) {
      return "Biển số xe chỉ được nhập chữ và số (VD: 30A12345)";
    }
    if (!Utils.validateLicensePlatesCarRemoveCharSpecial(form.licensePlates)) {
      return "Biển số xe không đúng định dạng ô tô";
    }
    return "";
  }, [form.licensePlates, fieldTouched.licensePlates]);

  const carBrandError = useMemo(() => {
    if (!fieldTouched.carBrand) return "";
    if (!form.carBrand?.code) return "Vui lòng chọn Hãng xe";
    return "";
  }, [form.carBrand, fieldTouched.carBrand]);

  const carModelError = useMemo(() => {
    if (!fieldTouched.carModel) return "";
    if (!form.carModel?.code) return "Vui lòng chọn Hiệu xe";
    return "";
  }, [form.carModel, fieldTouched.carModel]);

  const chassisNoError = useMemo(() => {
    if (!fieldTouched.chassisNo) return "";
    if (!form.chassisNo?.trim()) return "Vui lòng nhập số khung";
    if (form.chassisNo && Utils.validateChassisNo(form.chassisNo)) {
      return "Số khung không hợp lệ (chỉ được nhập chữ cái tiếng Việt không dấu và số, có ít nhất 6 ký tự và tối đa 30 ký tự)";
    }
    return "";
  }, [form.chassisNo, fieldTouched.chassisNo]);

  const engineNoError = useMemo(() => {
    if (!fieldTouched.engineNo) return "";
    if (!form.engineNo?.trim()) return "Vui lòng nhập số máy";
    if (form.engineNo && Utils.validateEngineNo(form.engineNo)) {
      return "Số máy không hợp lệ (chỉ được nhập chữ cái tiếng Việt không dấu và số, có ít nhất 6 ký tự và tối đa 30 ký tự)";
    }
    return "";
  }, [form.engineNo, fieldTouched.engineNo]);

  const buyerNameError = useMemo(() => {
    if (!fieldTouched.buyerName) return "";
    if (!form.buyerName?.trim()) return "Vui lòng nhập họ tên người mua";
    if (!Utils.validateName(form.buyerName.trim())) return "Họ tên không hợp lệ";
    return "";
  }, [form.buyerName, fieldTouched.buyerName]);

  const buyerPhoneError = useMemo(() => {
    if (!fieldTouched.buyerPhone) return "";
    if (!form.buyerPhone?.trim()) return "Vui lòng nhập số điện thoại";
    if (!Utils.validateVietnamPhone(form.buyerPhone)) {
      return "Số điện thoại không hợp lệ (VD: 0912345678)";
    }
    return "";
  }, [form.buyerPhone, fieldTouched.buyerPhone]);

  const emailError = useMemo(() => {
    if (!fieldTouched.email) return "";
    // Email không bắt buộc, chỉ validate khi có nhập
    if (form.email?.trim() && !Utils.validateEmail(form.email.trim())) {
      return "Email không hợp lệ (VD: example@email.com)";
    }
    return "";
  }, [form.email, fieldTouched.email]);

  const addressError = useMemo(() => {
    if (!fieldTouched.address) return "";
    if (!form.address?.trim()) return "Vui lòng nhập địa chỉ (số nhà, tên đường)";
    return "";
  }, [form.address, fieldTouched.address]);

  // Validations cho form hóa đơn VAT
  const invoiceCompanyNameError = useMemo(() => {
    if (!form.hasInvoice || !fieldTouched.invoiceCompanyName) return "";
    if (!form.invoiceCompanyName?.trim()) return "Vui lòng nhập tên công ty";
    return "";
  }, [form.hasInvoice, form.invoiceCompanyName, fieldTouched.invoiceCompanyName]);

  const invoiceTaxCodeError = useMemo(() => {
    if (!form.hasInvoice || !fieldTouched.invoiceTaxCode) return "";
    if (!form.invoiceTaxCode?.trim()) return "Vui lòng nhập mã số thuế";
    if (!/^[0-9\-]{10,14}$/.test(form.invoiceTaxCode.trim())) {
      return "Mã số thuế không hợp lệ (10 - 13 chữ số)";
    }
    return "";
  }, [form.hasInvoice, form.invoiceTaxCode, fieldTouched.invoiceTaxCode]);

  const invoiceCompanyAddressError = useMemo(() => {
    if (!form.hasInvoice || !fieldTouched.invoiceCompanyAddress) return "";
    if (!form.invoiceCompanyAddress?.trim()) return "Vui lòng nhập địa chỉ công ty";
    return "";
  }, [form.hasInvoice, form.invoiceCompanyAddress, fieldTouched.invoiceCompanyAddress]);

  const invoicePhoneError = useMemo(() => {
    if (!form.hasInvoice || !fieldTouched.invoicePhone) return "";
    if (!form.invoicePhone?.trim()) return "Vui lòng nhập số điện thoại";
    if (!Utils.validateVietnamPhone(form.invoicePhone)) {
      return "Số điện thoại không hợp lệ (VD: 0912345678)";
    }
    return "";
  }, [form.hasInvoice, form.invoicePhone, fieldTouched.invoicePhone]);

  const invoiceEmailError = useMemo(() => {
    if (!form.hasInvoice || !fieldTouched.invoiceEmail) return "";
    if (!form.invoiceEmail?.trim()) return "Vui lòng nhập email nhận hóa đơn";
    if (!Utils.validateEmail(form.invoiceEmail.trim())) return "Email không hợp lệ";
    return "";
  }, [form.hasInvoice, form.invoiceEmail, fieldTouched.invoiceEmail]);

  const invoiceRecipientNameError = useMemo(() => {
    if (!form.hasInvoice || !fieldTouched.invoiceRecipientName) return "";
    if (!form.invoiceRecipientName?.trim()) return "Vui lòng nhập tên người nhận";
    if (!Utils.validateName(form.invoiceRecipientName.trim())) return "Họ tên người nhận không hợp lệ";
    return "";
  }, [form.hasInvoice, form.invoiceRecipientName, fieldTouched.invoiceRecipientName]);

  const invoiceProvinceError = useMemo(() => {
    if (!form.hasInvoice || !fieldTouched.invoiceProvince) return "";
    if (!form.invoiceProvince?.code) return "Vui lòng chọn tỉnh thành";
    return "";
  }, [form.hasInvoice, form.invoiceProvince, fieldTouched.invoiceProvince]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleToggleIsOwner = (checked) => {
    if (checked) {
      const oauthBuyerName = userInfo?.user_name || userInfo?.displayName || customerInfo?.fullName || "";
      const oauthBuyerPhone = userInfo?.user_phone || customerInfo?.phone || "";
      const oauthEmail = userInfo?.user_email || customerInfo?.email || "";

      setForm((prev) => ({
        ...prev,
        isOwner: true,
        buyerName: oauthBuyerName,
        buyerPhone: oauthBuyerPhone,
        email: oauthEmail,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        isOwner: false,
        buyerName: "",
        buyerPhone: "",
        email: "",
      }));
    }

    setFieldTouched((prev) => ({
      ...prev,
      buyerName: false,
      buyerPhone: false,
      email: false,
    }));
  };

  // ─── Buy Submit Handler ───────────────────────────────────────────
  const handleBuy = () => {
    // Touch all fields to show validation messages if invalid
    setFieldTouched({
      carOwner: true,
      licensePlates: true,
      carBrand: true,
      carModel: true,
      chassisNo: true,
      engineNo: true,
      buyerName: true,
      buyerPhone: true,
      email: true,
      address: true,
      invoiceCompanyName: true,
      invoiceTaxCode: true,
      invoiceCompanyAddress: true,
      invoicePhone: true,
      invoiceEmail: true,
      invoiceRecipientName: true,
      invoiceProvince: true,
    });

    ZaloPaySDK.showLoading();

    // Check seating limits
    if (seatsError) {
      ZaloPaySDK.hideLoading();
      ZaloPaySDK.showDialog({
        message: seatsError,
        buttons: [{ text: "Đóng" }],
      });
      return;
    }

    const hasInvalidInvoice = form.hasInvoice && (
      !form.invoiceCompanyName?.trim() ||
      !form.invoiceTaxCode?.trim() ||
      !/^[0-9\-]{10,14}$/.test(form.invoiceTaxCode.trim()) ||
      !form.invoiceCompanyAddress?.trim() ||
      !form.invoicePhone?.trim() ||
      !Utils.validateVietnamPhone(form.invoicePhone) ||
      !form.invoiceEmail?.trim() ||
      !Utils.validateEmail(form.invoiceEmail.trim()) ||
      !form.invoiceRecipientName?.trim() ||
      !Utils.validateName(form.invoiceRecipientName.trim()) ||
      !form.invoiceProvince?.code
    );

    const hasInvalidEmail = Boolean(
      form.email?.trim() && !Utils.validateEmail(form.email.trim())
    );

    const hasInvalidForm =
      !form.carOwner?.trim() ||
      !form.licensePlates?.trim() ||
      !form.carBrand?.code ||
      !form.carModel?.code ||
      !form.buyerName?.trim() ||
      !form.buyerPhone?.trim() ||
      !form.address?.trim() ||
      !Utils.validateVietnamPhone(form.buyerPhone) ||
      hasInvalidEmail ||
      Utils.validateChassisNo(form.chassisNo) ||
      Utils.validateEngineNo(form.engineNo) ||
      hasInvalidInvoice;

    if (hasInvalidForm) {
      ZaloPaySDK.hideLoading();
      ZaloPaySDK.showDialog({
        message: hasInvalidInvoice
          ? "Vui lòng kiểm tra và điền đầy đủ các thông tin xuất hóa đơn (*)."
          : "Vui lòng kiểm tra và điền đầy đủ các thông tin bắt buộc (*).",
        buttons: [{ text: "Đóng" }],
      });
      return;
    }

    // Format formatted license plates
    const formattedPlates = Utils._convertLicensePlatesCar(form.licensePlates);

    const vehicleInfo = {
      carOwner: form.carOwner.toUpperCase(),
      licensePlates: formattedPlates,
      carBrand: form.carBrand,
      carModel: form.carModel,
      chassisNo: form.chassisNo,
      engineNo: form.engineNo,
      seat: Number(seats) || 5,
    };

    const contractInfo = {
      fullName: form.buyerName,
      phone: form.buyerPhone,
      email: form.email,
      address: `${form.address}`,
      dateEffect: dateEffect,
      ...(form.hasInvoice ? {
        isVat: true,
        vatInfo: {
          companyName: form.invoiceCompanyName,
          taxCode: form.invoiceTaxCode,
          companyAddress: form.invoiceCompanyAddress,
          phone: form.invoicePhone,
          email: form.invoiceEmail,
          recipientName: form.invoiceRecipientName,
          province: form.invoiceProvince,
        }
      } : {})
    };

    const insuranceDetailPayload = selectedProduct || {
      company: { code: selectedCompanyCode, name: selectedCompanyObj.name },
      premium: totalFee,
      usePurpose: selectedCarType?.code?.usePurpose || "non_commercial",
      carInfo: (selectedCarType?.code?.carInfo || 1) + "",
      seat: Number(seats) || 5,
    };

    dispatch(
      createOrderCallback(
        TYPE_INSURANCE.CAR,
        insuranceDetailPayload,
        contractInfo,
        vehicleInfo,
        undefined,
        (resp) => {
          const { id } = resp;
          console.log("Order created successfully, order ID:", id);
          dispatch(payOrder(id, PRODUCT_TYPE.car));
        }
      )
    );
  };

  return (
    <div className="car-insurance">
      {/* 1. SECTION LOẠI XE (TierBar Pills ở trên cùng) */}
      <CarTierBar
        selectedCarType={selectedCarType}
        onSelectCarType={(typeItem) => {
          setSelectedCarType(typeItem);
          const isMic = selectedCompanyCode === "MIC";
          if (typeItem.code.carInfo === 19) {
            const nextSeats = isMic ? "4" : "2";
            setSeats(nextSeats);
            setSeatsCommitted(nextSeats);
          } else if (Number(seats) < (isMic ? 4 : 2)) {
            setSeats("5");
            setSeatsCommitted("5");
          }
        }}
      />

      <div className="car-insurance__body">
        {/* 2. GÓI BẢO HIỂM PANEL (Công ty bảo hiểm, Số chỗ ngồi, Ngày hiệu lực, Bảo hiểm tai nạn, Quyền lợi nổi bật) */}
        <CarInsurancePanel
          companies={companies}
          selectedCompanyCode={selectedCompanyCode}
          onSelectCompany={(compObj) => {
            const newCode = compObj.code || compObj.id;
            if (newCode !== selectedCompanyCode) {
              setSelectedCompanyCode(newCode);
              // Reset Hãng xe & Hiệu xe khi change công ty bảo hiểm
              setForm((prev) => ({
                ...prev,
                carBrand: {},
                carModel: {},
              }));
              setFieldTouched((prev) => ({
                ...prev,
                carBrand: false,
                carModel: false,
              }));
              setCarBrandData([]);
              setCarModelData([]);

              if (newCode === "MIC" && Number(seats) < 4) {
                setSeats("5");
                setSeatsCommitted("5");
              }
            }
          }}
          seats={seats}
          onSeatsChange={setSeats}
          onSeatsBlur={handleSeatsBlur}
          seatsError={seatsError}
          dateEffect={dateEffect}
          onDateChange={setDateEffect}
          minDateEffect={minDateEffect}
          maxDateEffect={maxDateEffect}
          accidentInsurance={accidentInsurance}
          onAccidentInsuranceChange={setAccidentInsurance}
          onOpenBenefitSheet={openBenefitSheet}
        />

        {/* 3. THÔNG TIN CHỦ XE, BIỂU MẪU KHÁCH HÀNG & HÓA ĐƠN VAT */}
        <CarOwnerInfoSection
          form={form}
          updateField={updateField}
          setFieldTouched={setFieldTouched}
          onToggleIsOwner={handleToggleIsOwner}
          carOwnerError={carOwnerError}
          licensePlatesError={licensePlatesError}
          carBrandError={carBrandError}
          carModelError={carModelError}
          chassisNoError={chassisNoError}
          engineNoError={engineNoError}
          buyerNameError={buyerNameError}
          buyerPhoneError={buyerPhoneError}
          emailError={emailError}
          addressError={addressError}
          invoiceCompanyNameError={invoiceCompanyNameError}
          invoiceTaxCodeError={invoiceTaxCodeError}
          invoiceCompanyAddressError={invoiceCompanyAddressError}
          invoicePhoneError={invoicePhoneError}
          invoiceEmailError={invoiceEmailError}
          invoiceRecipientNameError={invoiceRecipientNameError}
          invoiceProvinceError={invoiceProvinceError}
          carBrands={carBrandData}
          carModels={carModelData}
          provinces={provincesData}
          loadingBrands={loadingBrands}
          loadingModels={loadingModels}
          handleBrandChange={handleBrandChange}
          handleModelChange={handleModelChange}
        />
      </div>

      {/* 4. MODAL CHI TIẾT QUYỀN LỢI BẢO HIỂM */}
      <CarBenefitSheetModal
        isOpen={benefitSheetOpen}
        onClose={closeBenefitSheet}
        activeTab={benefitSheetTab}
        onTabChange={setBenefitSheetTab}
      />

      {/* 5. FOOTER CỐ ĐỊNH BOTTOM THANH TOÁN */}
      <footer className="car-insurance__footer">
        <div className="car-insurance__footer-fee-wrap">
          <span className="car-insurance__footer-fee-label">Tổng phí thanh toán:</span>
          <span className="car-insurance__footer-fee-value">
            {Utils.formatMoneyVND(totalFee)}đ
          </span>
        </div>

        <button
          type="button"
          className="car-insurance__buy-btn"
          onClick={handleBuy}
        >
          Mua ngay
        </button>
      </footer>
    </div>
  );
};

export default CarInsuranceScreen;

